import axios, { type AxiosError, type AxiosInstance } from 'axios';
import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import { env } from '../env';
import { useCheckTimeManipulation } from '@group-link-one/gl-components';
import { onRequestError } from '../utils/onError';
import { useAuth } from '@/Bridges/GLUtils';
import { IMPERSONATE_KEY } from '@/Constants/keys';
import { useBlockedAccount } from '@/Contexts/BlockedAccount';
import { useModalLogout } from '@/Contexts/GLModalLogout';

declare module 'axios' {
  interface AxiosRequestConfig {
    /**
     * Set once a request went through the clock re-sync after an `INVALID_TIMESTAMP`
     * (retried, or given up because the re-sync failed): it is never retried twice,
     * and its `INVALID_TIMESTAMP` is reported to Sentry (see `onRequestError`).
     */
    _clockRetried?: boolean;
  }
}

// In-flight clock sync, shared by the boot sync (`useRouteEffects`) and every
// request that hits `INVALID_TIMESTAMP` meanwhile, so they trigger a single trace fetch.
let clockSync: Promise<number | null> | null = null;

/**
 * Measure the clock offset against the API's `/cdn-cgi/trace`, joining the sync
 * already in flight if there is one. Resolves the new offset, or `null` when the
 * sync failed (the stored offset is then unchanged). Never rejects.
 */
export function syncClockOffset(
  syncOffsetFromTrace: (baseUrl: string) => Promise<number | null>,
): Promise<number | null> {
  clockSync ??= syncOffsetFromTrace(env.VITE_REACT_APP_API_URL).finally(() => {
    clockSync = null;
  });
  return clockSync;
}

interface AxiosHTTPRequest {
  http: AxiosInstance;
  httpPrivate: AxiosInstance;
}

const useAxiosHTTPRequest = (): AxiosHTTPRequest => {
  const navigate = useNavigate();
  const { accessToken, accessTokenIsValid } = useAuth();
  const { handleOpenModalLogout } = useModalLogout();
  const { setAccountBlocked } = useBlockedAccount();
  const { syncOffsetFromTrace, getAdjustedTimestamp } = useCheckTimeManipulation();

  const api = axios.create({
    baseURL: env.VITE_REACT_APP_API_URL,
    headers: {
      'Content-Type': 'application/json',
    },
  });

  const apiPrivate = axios.create({
    baseURL: env.VITE_REACT_APP_API_URL,
    headers: {
      'Content-Type': 'application/json',
      Authorization: accessToken,
    },
  });

  useEffect(() => {
    api.interceptors.request.use(config => {
      config.headers['X-Timestamp'] = getAdjustedTimestamp();
      return config;
    });

    apiPrivate.interceptors.request.use(config => {
      if (!accessTokenIsValid) {
        const source = axios.CancelToken.source();
        config.cancelToken = source.token;
        source.cancel('Access token is invalid');
      }

      config.headers.Authorization = accessToken;
      config.headers['X-Timestamp'] = getAdjustedTimestamp();
      return config;
    });

    // `X-Timestamp` was rejected: re-measure the clock offset and retry once; the
    // request interceptor re-stamps the retry. If the re-sync failed the offset did
    // not change, so a retry would carry the same rejected stamp: give up instead.
    // Either way the error that ends it goes to the caller and to Sentry.
    const handleInvalidTimestamp = async (error: AxiosError, axiosInstance: AxiosInstance) => {
      const originalConfig = error.config;

      if (!originalConfig || originalConfig._clockRetried) {
        return Promise.reject(error);
      }

      const offset = await syncClockOffset(syncOffsetFromTrace);
      originalConfig._clockRetried = true;

      if (offset === null) {
        // The interceptor skipped Sentry for this first attempt, expecting a retry.
        onRequestError(error);
        return Promise.reject(error);
      }

      return axiosInstance(originalConfig);
    };

    api.interceptors.response.use(
      response => {
        return response;
      },
      error => {
        onRequestError(error);

        if (error.response?.data?.error === 'INVALID_TIMESTAMP') {
          return handleInvalidTimestamp(error, api);
        }

        return Promise.reject(error);
      },
    );

    apiPrivate.interceptors.response.use(
      response => {
        return response;
      },
      error => {
        onRequestError(error);

        if (error.response?.status === 402) {
          setAccountBlocked(true);
          navigate('/');
          return;
        }

        if (
          error.response?.status === 401 &&
          !error.response?.data?.message?.includes('privilégios')
        ) {
          handleOpenModalLogout();
          localStorage.removeItem(IMPERSONATE_KEY);
          return;
        }

        if (error.response?.data?.message?.includes('privilégios')) {
          navigate('/');
          return;
        }

        if (error.response?.data?.error === 'INVALID_TIMESTAMP') {
          return handleInvalidTimestamp(error, apiPrivate);
        }

        return Promise.reject(error);
      },
    );
  }, [
    accessToken,
    accessTokenIsValid,
    api,
    apiPrivate,
    getAdjustedTimestamp,
    handleOpenModalLogout,
    navigate,
    setAccountBlocked,
    syncOffsetFromTrace,
  ]);

  return {
    http: api,
    httpPrivate: apiPrivate,
  };
};

export default useAxiosHTTPRequest;
